import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { deleteColumn } from 'api/task/deleteColumn'
import { deleteTask } from 'api/task/deleteTask'
import { insertColumn } from 'api/task/insertColumn'
import { insertTask } from 'api/task/insertTask'
import { moveTask } from 'api/task/moveTask'
import { updateColumn } from 'api/task/updateColumn'
import { updateTask } from 'api/task/updateTask'
import { useColumns } from 'api/task/useColumns'
import { useAllCompany } from 'api/company/useAllCompany'
import { MainPage } from 'components/templates/MainPage'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { AuthState } from 'recoil/atoms/auth.atom'
import swal from 'sweetalert2'
import { ColumnModal } from './ColumnModal'
import { KanbanColumn } from './KanbanColumn'
import { TaskModal } from './TaskModal'
import { moveTaskInBoard } from './boardUtils'
import { Column, Task } from './types'

export const TasksPage = (): JSX.Element => {
  const auth = useRecoilValue(AuthState)
  const isAdmin = auth === 'ADMIN'

  const userCode = useMemo(() => {
    const stored = localStorage.getItem('auth')
    if (!stored) return ''
    try {
      return (JSON.parse(stored) as { userCode?: string }).userCode ?? ''
    } catch {
      return ''
    }
  }, [])

  const { data: columnsData, mutate } = useColumns()
  const { data: companiesData } = useAllCompany()

  const companies = useMemo(
    () => (companiesData ?? []).map((c) => ({ id: c.id, name: c.name })),
    [companiesData]
  )

  const companyMap = useMemo(
    () =>
      companies.reduce<Record<number, string>>((acc, c) => {
        acc[c.id] = c.name
        return acc
      }, {}),
    [companies]
  )

  // API レスポンスをローカル状態に変換
  const columns: Column[] = useMemo(
    () => columnsData.map((c) => ({ id: c.id, title: c.title })),
    [columnsData]
  )
  const tasks: Task[] = useMemo(
    () =>
      columnsData.flatMap((c) =>
        c.tasks.map((t) => ({
          id: t.id,
          columnId: c.id,
          companyId: t.companyId ?? null,
          title: t.title,
          description: t.description ?? '',
          dueDate: t.dueDate ?? '',
          creator: t.creator,
          isSecret: t.isSecret,
        }))
      ),
    [columnsData]
  )

  // モーダル状態
  const [taskModal, setTaskModal] = useState<{
    open: boolean
    columnId: string
    task?: Task
  }>({ open: false, columnId: '' })

  const [columnModal, setColumnModal] = useState<{
    open: boolean
    column?: Column
  }>({ open: false })

  // DnD：ドロップ確定後に boardTasks を更新する（ドラッグ中は API データのまま表示）
  const [boardTasks, setBoardTasks] = useState<Task[]>(tasks)

  useEffect(() => {
    setBoardTasks(tasks)
  }, [tasks])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  // 権限に応じてタスクをフィルタリング（一般ユーザーはシークレットタスクを参照不可）
  const visibleTasks = useMemo(
    () => (isAdmin ? boardTasks : boardTasks.filter((t) => !t.isSecret)),
    [isAdmin, boardTasks]
  )

  const tasksByColumn = useCallback(
    (columnId: string) => visibleTasks.filter((t) => t.columnId === columnId),
    [visibleTasks]
  )

  const applyTaskOptimisticUpdate = useCallback(
    async (
      nextTasks: Task[],
      request: () => Promise<unknown>,
      errorText: string
    ) => {
      const previousTasks = boardTasks
      setBoardTasks(nextTasks)

      try {
        await request()
        await mutate()
        return true
      } catch {
        setBoardTasks(previousTasks)
        swal.fire({
          icon: 'error',
          text: errorText,
          confirmButtonText: 'OK'
        })
        return false
      }
    },
    [boardTasks, mutate]
  )

  // ─── DnD ハンドラ ─────────────────────────────────────────────────

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string
    if (activeId === overId) return

    const overType = over.data.current?.type as string | undefined

    // 現在表示中の状態をベースに移動後の配列を計算してドロップ時に一括反映
    const newTasks = moveTaskInBoard(boardTasks, activeId, overId, overType)

    const movedTask = newTasks.find((task) => task.id === activeId)
    if (!movedTask) return

    const columnTasks = newTasks.filter(
      (task) => task.columnId === movedTask.columnId
    )
    const position = columnTasks.findIndex((task) => task.id === movedTask.id)

    await applyTaskOptimisticUpdate(
      newTasks,
      async () =>
        moveTask({
          taskId: movedTask.id,
          targetColumnId: movedTask.columnId,
          position: position >= 0 ? position : columnTasks.length
        }),
      'タスクの移動に失敗しました。'
    )
  }

  // ─── タスク操作 ────────────────────────────────────────────────────

  const handleSaveTask = async (data: Omit<Task, 'id'> & { id?: string }) => {
    try {
      if (data.id) {
        const taskId = data.id
        const nextTasks = boardTasks.map((task) =>
          task.id === taskId
            ? {
                ...task,
                columnId: data.columnId,
                companyId: data.companyId,
                title: data.title,
                description: data.description,
                dueDate: data.dueDate,
                creator: data.creator,
                isSecret: data.isSecret,
              }
            : task
        )

        const ok = await applyTaskOptimisticUpdate(
          nextTasks,
          async () =>
            updateTask({
              id: taskId,
              columnId: data.columnId,
              companyId: data.companyId ?? undefined,
              title: data.title,
              description: data.description || undefined,
              dueDate: data.dueDate || undefined,
              creator: data.creator,
              isSecret: data.isSecret,
              updatedBy: userCode
            }),
          'タスクの保存に失敗しました。'
        )

        if (!ok) return
      } else {
        const tempTask: Task = {
          id: `temp-${Date.now()}`,
          columnId: data.columnId,
          companyId: data.companyId,
          title: data.title,
          description: data.description,
          dueDate: data.dueDate,
          creator: data.creator,
          isSecret: data.isSecret,
        }

        const ok = await applyTaskOptimisticUpdate(
          [...boardTasks, tempTask],
          async () =>
            insertTask({
              columnId: data.columnId,
              companyId: data.companyId ?? undefined,
              title: data.title,
              description: data.description || undefined,
              dueDate: data.dueDate || undefined,
              creator: data.creator,
              isSecret: data.isSecret,
            }),
          'タスクの保存に失敗しました。'
        )

        if (!ok) return
      }

      setTaskModal({ open: false, columnId: '' })
    } catch {
      swal.fire({
        icon: 'error',
        text: 'タスクの保存に失敗しました。',
        confirmButtonText: 'OK'
      })
    }
  }

  const handleDeleteTask = async (taskId: string) => {
    const result = await swal.fire({
      icon: 'warning',
      text: 'このタスクを削除しますか？',
      showCancelButton: true,
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
      confirmButtonColor: '#ef4444'
    })
    if (!result.isConfirmed) return

    await applyTaskOptimisticUpdate(
      boardTasks.filter((task) => task.id !== taskId),
      async () => deleteTask({ id: taskId }),
      'タスクの削除に失敗しました。'
    )
  }

  // ─── カラム操作 ────────────────────────────────────────────────────

  const handleSaveColumn = async (title: string, id?: string) => {
    try {
      if (id) {
        await updateColumn({ id, title })
      } else {
        await insertColumn({ title })
      }
      await mutate()
      setColumnModal({ open: false })
    } catch {
      swal.fire({
        icon: 'error',
        text: 'カラムの保存に失敗しました。',
        confirmButtonText: 'OK'
      })
    }
  }

  const handleDeleteColumn = async (columnId: string) => {
    const hasTasks = tasks.some((t) => t.columnId === columnId)
    const text = hasTasks
      ? 'このカラムにはタスクがあります。カラムごと削除しますか？'
      : 'このカラムを削除しますか？'
    const result = await swal.fire({
      icon: 'warning',
      text,
      showCancelButton: true,
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
      confirmButtonColor: '#ef4444'
    })
    if (!result.isConfirmed) return
    try {
      await deleteColumn({ id: columnId })
      await mutate()
    } catch {
      swal.fire({
        icon: 'error',
        text: 'カラムの削除に失敗しました。',
        confirmButtonText: 'OK'
      })
    }
  }

  // ─── レンダリング ──────────────────────────────────────────────────

  return (
    <MainPage>
      <div className="flex h-full flex-col">
        {/* ページヘッダー */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setColumnModal({ open: true })}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              ＋ カラムを追加
            </button>
          </div>
        </div>

        {/* カンバンボード */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragEnd={onDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-4">
            {columns.map((column) => (
              <KanbanColumn
                key={column.id}
                column={column}
                tasks={tasksByColumn(column.id)}
                companyMap={companyMap}
                onAddTask={(colId) =>
                  setTaskModal({ open: true, columnId: colId })
                }
                onEditTask={(task) =>
                  setTaskModal({ open: true, columnId: task.columnId, task })
                }
                onDeleteTask={handleDeleteTask}
                onEditColumn={(col) =>
                  setColumnModal({ open: true, column: col })
                }
                onDeleteColumn={handleDeleteColumn}
              />
            ))}

            {/* 空の場合のメッセージ */}
            {columns.length === 0 && (
              <div className="flex w-full items-center justify-center py-20 text-gray-400">
                カラムがありません。「＋
                カラムを追加」からカラムを作成してください。
              </div>
            )}
          </div>
        </DndContext>

        {/* タスクモーダル */}
        {taskModal.open && (
          <TaskModal
            columns={columns}
            companies={companies}
            initialColumnId={taskModal.columnId}
            isAdmin={isAdmin}
            creator={userCode}
            task={taskModal.task}
            onClose={() => setTaskModal({ open: false, columnId: '' })}
            onSave={handleSaveTask}
          />
        )}

        {/* カラムモーダル */}
        {columnModal.open && (
          <ColumnModal
            column={columnModal.column}
            onClose={() => setColumnModal({ open: false })}
            onSave={handleSaveColumn}
          />
        )}
      </div>
    </MainPage>
  )
}
